import { css } from '@emotion/react';
import {
  ConflictRoundInfo,
  EventTypes,
  GameEventOfType,
} from '@mythos/game/GameEvents';
import { sortBy } from 'lodash';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { match, P } from 'ts-pattern';
import { EffectPill } from './CardHelpers';
import { UIColorPalette } from './ColorPalette';
import DieView from './DieView';
import { UserWithColor } from './GraphQLHelpers';
import Symbols, { SymbolView } from './Symbols';

// State machine for conflict results animation
type AnimState =
  | { phase: 'idle' }
  | { phase: 'rolling'; round: number }
  | { phase: 'round_results'; round: number; advanceAt: number }
  | { phase: 'done' };

type AnimEvent =
  | { type: 'START' }
  | { type: 'DICE_SETTLED' }
  | { type: 'TIMER_FIRED' };

function roundHasDice(round: ConflictRoundInfo): boolean {
  return Object.values(round).some((p) => p.rolls.length > 0);
}

function transition(
  state: AnimState,
  event: AnimEvent,
  ctx: { rounds: ConflictRoundInfo[]; numPlayers: number },
): AnimState {
  return match(state)
    .with({ phase: 'idle' }, (s) =>
      match(event)
        .with({ type: 'START' }, () => {
          if (ctx.numPlayers <= 1) {
            return { phase: 'done' } as const;
          }
          if (roundHasDice(ctx.rounds[0])) {
            return { phase: 'rolling', round: 0 } as const;
          }
          // No dice - go straight to round_results
          return {
            phase: 'round_results',
            round: 0,
            advanceAt: Date.now() + 800,
          } as const;
        })
        .otherwise(() => s),
    )
    .with({ phase: 'rolling' }, (s) =>
      match(event)
        .with({ type: 'DICE_SETTLED' }, () => {
          return {
            phase: 'round_results',
            round: s.round,
            advanceAt: Date.now() + 1500,
          } as const;
        })
        .otherwise(() => s),
    )
    .with({ phase: 'round_results' }, (s) =>
      match(event)
        .with({ type: 'TIMER_FIRED' }, () => {
          const nextRound = s.round + 1;
          if (nextRound >= ctx.rounds.length) {
            return { phase: 'done' } as const;
          }
          if (roundHasDice(ctx.rounds[nextRound])) {
            return { phase: 'rolling', round: nextRound } as const;
          }
          return {
            phase: 'round_results',
            round: nextRound,
            advanceAt: Date.now() + 800,
          } as const;
        })
        .otherwise(() => s),
    )
    .with({ phase: 'done' }, (s) => s)
    .exhaustive();
}

export type ConflictResultsDisplayRef = {
  startAnimation: () => void;
};
type ConflictResultsDisplayProps = {
  rounds: ConflictRoundInfo[];
  winningPlayerID?: string | null;
  userByID: Map<string, UserWithColor>;
  onCompleted: () => void;
  animate: boolean;
};
export const ConflictResultsDisplay = forwardRef<
  ConflictResultsDisplayRef,
  ConflictResultsDisplayProps
>((props, ref) => {
  const { rounds, winningPlayerID, userByID } = props;

  const firstRound = rounds[0];
  let playerIDs = Object.keys(firstRound);
  const playerIDToBonus = Object.fromEntries(
    playerIDs.map((playerID) => [playerID, firstRound[playerID].bonus]),
  );

  playerIDs = sortBy(playerIDs, (playerID) => {
    return -playerIDToBonus[playerID];
  });

  const ctx = { rounds, numPlayers: playerIDs.length };

  // Initialize state based on whether we should animate
  const [state, setState] = useState<AnimState>(() =>
    props.animate ? { phase: 'idle' } : { phase: 'done' },
  );

  const dispatch = (event: AnimEvent) => {
    setState((s) => transition(s, event, ctx));
  };

  // Imperative API for parent to trigger animation
  const startedRef = useRef(false);
  const startAnimation = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    dispatch({ type: 'START' });
  };

  useImperativeHandle(ref, () => ({ startAnimation }));

  // Timer effect for round_results -> next state
  useEffect(() => {
    if (state.phase !== 'round_results') return;
    const delay = Math.max(0, state.advanceAt - Date.now());
    const t = setTimeout(() => dispatch({ type: 'TIMER_FIRED' }), delay);
    return () => clearTimeout(t);
  }, [state]);

  // Notify parent when done
  const completedRef = useRef(false);
  useEffect(() => {
    if (state.phase === 'done' && !completedRef.current) {
      completedRef.current = true;
      // Defer to ensure all refs are set in parent
      setTimeout(() => props.onCompleted(), 0);
    }
  }, [state.phase]);

  // Dice settled callback - only fire once per round
  const diceSettledForRound = useRef(-1);
  const onDiceSettled = () => {
    if (state.phase !== 'rolling') return;
    if (diceSettledForRound.current === state.round) return;
    diceSettledForRound.current = state.round;
    dispatch({ type: 'DICE_SETTLED' });
  };

  // Derive display values from state
  const currentRound = match(state)
    .with({ phase: 'idle' }, () => 0)
    .with({ phase: 'rolling', round: P.select() }, (r) => r)
    .with({ phase: 'round_results', round: P.select() }, (r) => r)
    .with({ phase: 'done' }, () => rounds.length - 1)
    .exhaustive();

  const round = rounds[currentRound];
  const isLastRound = currentRound === rounds.length - 1;
  const showDiceAnimating = state.phase === 'rolling';
  const showTotals = state.phase === 'round_results' || state.phase === 'done';

  return (
    <div css={ConflictResultsStyles.container}>
      {playerIDs.map((playerID) => {
        const playerRound = round?.[playerID];
        const total = !playerRound
          ? '💀'
          : showTotals
            ? playerRound.total
            : showDiceAnimating
              ? '?'
              : '';
        const isWinner = playerID === winningPlayerID;
        const isLoser = winningPlayerID && playerID !== winningPlayerID;
        return (
          <div
            key={playerID}
            css={ConflictResultsStyles.playerContainer}
            style={
              state.phase === 'done' && isLastRound
                ? {
                    backgroundColor: isLoser
                      ? UIColorPalette.conflictResultsLoserBG
                      : undefined,
                    order: isWinner ? -1 : 0,
                  }
                : {}
            }
          >
            <div css={ConflictResultsStyles.splitter}>
              <span
                css={ConflictResultsStyles.name}
                style={{ color: userByID.get(playerID)?.color || '' }}
              >
                {isWinner && state.phase === 'done' ? '\u2605' : ''}
                {userByID.get(playerID)?.name}
              </span>
              <span>{total}</span>
            </div>
            <div css={ConflictResultsStyles.splitter}>
              <span>
                <SymbolView
                  symbol={Symbols.MILITARY}
                  css={ConflictResultsStyles.militarySymbol}
                />
                {playerIDToBonus[playerID]}
              </span>
              <div>
                {state.phase !== 'idle' &&
                  playerRound?.rolls.map((roll, i) => (
                    <DieView
                      key={i}
                      roll={roll}
                      animate={showDiceAnimating}
                      onAnimateEnd={onDiceSettled}
                    />
                  ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export const ResolutionPhaseConflictResultsDisplay = (props: {
  event: GameEventOfType<EventTypes.CONFLICT_RESULTS>;
  userByID: Map<string, UserWithColor>;
}) => {
  const { event, userByID } = props;
  const firstRound = event.payload.rounds[0];
  let playerIDs = Object.keys(firstRound);
  const playerIDToBonus = Object.fromEntries(
    playerIDs.map((playerID) => [playerID, firstRound[playerID].bonus]),
  );

  playerIDs = sortBy(playerIDs, (playerID) => {
    if (event.payload.winningPlayerID === playerID) {
      return -Infinity;
    }
    return -playerIDToBonus[playerID];
  });

  return (
    <div css={ConflictResultsStyles.container}>
      {playerIDs.map((playerID) => {
        const isWinner = playerID === event.payload.winningPlayerID;
        return (
          <div
            key={playerID}
            css={ConflictResultsStyles.playerContainer}
            style={{
              opacity: !isWinner ? 0.5 : 1,
            }}
          >
            <div css={ConflictResultsStyles.splitter}>
              <span
                css={ConflictResultsStyles.name}
                style={{ color: userByID.get(playerID)?.color || '' }}
              >
                {isWinner ? '\u2605' : ''}
                {userByID.get(playerID)?.name}
              </span>
              <EffectPill
                output={{
                  outputResource: 'military',
                  outputRatio: playerIDToBonus[playerID],
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ConflictResultsStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',

    backgroundColor: UIColorPalette.conflictResultsBG,
    borderRadius: 4,
    padding: 4,

    width: 80,
  }),
  splitter: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  }),
  playerContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',

    fontSize: 12,
    paddingTop: 1,
    paddingBottom: 1,

    '&:not(:last-child)': {
      marginBottom: 2,
    },
    transition: 'background-color 0.5s, order 1.5s',
  }),
  name: css({
    fontSize: 12,
    fontWeight: 'bold',
    paddingBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  militarySymbol: css({
    height: 18,
    width: 'auto',
    margin: 2,
    verticalAlign: 'middle',
    marginTop: 0,
  }),
};
